import { useEffect } from 'react';
import { useI18n } from '../lib/i18n';
import type { DataFile } from '../types';

export function MethodologyModal({
  open,
  onClose,
  data,
}: {
  open: boolean;
  onClose: () => void;
  data: DataFile | null;
}) {
  const { t, lang } = useI18n();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const pulled = data?.pulled_at
    ? new Intl.DateTimeFormat(lang === 'es' ? 'es-PR' : 'en-US', {
        dateStyle: 'long',
      }).format(new Date(data.pulled_at))
    : '—';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="methodology-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-an-black/70 px-4"
      onClick={onClose}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto border border-an-rule bg-an-white p-6 helix-bg"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label={t('app.methodology.close')}
          className="display absolute right-3 top-3 border border-an-rule bg-an-white px-2 py-1 text-[11px] uppercase hover:bg-an-black hover:text-an-white"
        >
          ×
        </button>
        <h2 id="methodology-title" className="display text-3xl uppercase">
          {t('app.methodology.title')}
        </h2>
        <div className="mt-4 h-px w-full bg-an-rule" />
        <p className="mt-4 font-body text-sm leading-relaxed">
          {t('app.methodology.body')}
        </p>
        <dl className="mt-6 space-y-2 font-body text-xs text-an-ink">
          <div className="flex gap-2">
            <dt className="text-an-muted uppercase tracking-widest">
              {t('app.methodology.pulledAt')}:
            </dt>
            <dd>{pulled}</dd>
          </div>
          {data?.vintage && (
            <div className="flex gap-2">
              <dt className="text-an-muted uppercase tracking-widest">Vintage:</dt>
              <dd>{data.vintage}</dd>
            </div>
          )}
        </dl>
      </div>
    </div>
  );
}
